import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RestAtShoreButton } from '@/components/innerworld';
import { useKeepsakes } from '@/hooks/useInnerWorld';

export default function BookOfBecomingPage() {
  const navigate = useNavigate();
  const { keepsakes, includedCount, progress, loading, downloadKeepsake } = useKeepsakes();

  const handleDownload = async (k) => {
    try {
      await downloadKeepsake(k);
    } catch {
      toast.error('The book is resting — try again in a moment.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FBF9F6' }}>
        <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#C9A84C', boxShadow: '0 0 20px rgba(201,168,76,0.6)' }} />
      </div>
    );
  }

  const toward = progress?.entries_toward_next_keepsake ?? 0;
  const trigger = progress?.keepsake_trigger_count ?? 20;

  return (
    <div className="min-h-screen pb-16" style={{ background: '#FBF9F6' }} data-testid="book-of-becoming-screen">
      <header className="max-w-md mx-auto px-6 pt-8 pb-2 flex items-center justify-between">
        <button onClick={() => navigate('/inner-world')} className="flex items-center gap-2 text-xs tracking-[0.1em]" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }} data-testid="book-back-button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 18l-6-6 6-6" /></svg>
          THE MAP
        </button>
        <RestAtShoreButton />
      </header>

      <main className="max-w-md mx-auto px-6 pt-6">
        <div className="text-center mb-10">
          <span className="text-xs tracking-[0.2em] block mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>THE BOOK OF BECOMING</span>
          <p className="text-base leading-relaxed" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
            A keepsake of your journey — the reflections you chose to keep, your Council, and the themes that appeared in your own words.
          </p>
        </div>

        <div className="p-5 rounded-2xl mb-6" style={{ background: '#F7F1EE' }} data-testid="keepsake-progress-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.1em]" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>NEXT CHAPTER</span>
            <span className="text-xs" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}>{toward} of {trigger} reflections</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F3EEE8' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, (toward / trigger) * 100)}%`, background: 'linear-gradient(to right, rgba(201,168,76,0.5), #C9A84C)' }} />
          </div>
          <p className="text-[11px] mt-3 leading-relaxed" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}>
            A new chapter is offered every {trigger} reflections. {includedCount} of your reflections are marked to be kept.
          </p>
        </div>

        {keepsakes.length === 0 ? (
          <div className="text-center py-10" data-testid="keepsakes-empty-state">
            <p className="text-base leading-relaxed" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
              Your first chapter is still being written.
            </p>
            <p className="text-sm mt-3" style={{ fontFamily: 'Jost, sans-serif', color: '#8A6A61' }}>
              Keep reflecting — the book gathers itself.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {keepsakes.map((k) => (
              <div key={k.id} className="p-5 rounded-2xl flex items-center justify-between" style={{ background: '#F7F1EE' }} data-testid={`keepsake-${k.id}`}>
                <div>
                  <p className="text-sm mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
                    Chapter at {k.milestone_count} reflections
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}>
                    {k.generated_at?.slice(0, 10)}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(k)}
                  className="px-4 py-2 rounded-full text-xs tracking-[0.08em] transition-all active:scale-95"
                  style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}
                  data-testid={`keepsake-download-${k.id}`}
                >
                  DOWNLOAD PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
